import { EmbeddableCatalogBrowserWithoutRouter } from '@tableau/data-catalog-client-components';
import { WebClientApiContentTypeCountsResponse } from '@tableau/data-catalog-client-components/lib/src/components/CatalogBrowser/CatalogBrowserTypes';
import * as React from 'react';

import { ISpaceInfoTypeItemCount } from '~src/app/state/ContentCountsState';

import { SiteUrlsProps } from '../app/selectors/SiteUrlsSelectors';
import { ConnectedSpaceInfoProvider } from '../app/spaces/RouterHelpers/SpaceInfoProvider';
import { SpaceInfo, SpaceItemForNonEntitySpace } from '../app/state/SpaceInfo';
import { hideLaunchEditOnMobile } from '../app/utils/MobileHelper';
import { catalogFeatureFlagsAdapter } from '../catalog/CatalogFeatureFlagsAdapter';
import { DatabaseDefinition } from '../contentDefinitions/DatabaseDefinition';
import { TableDefinition } from '../contentDefinitions/TableDefinition';
import { FeatureFlags } from '../ts/FeatureFlags';
import { DatabaseContentType, TableContentType } from '../ts/models/ContentTypes';
import { DataCatalogUIActionsService } from '../ts/services/DataCatalogUIActionsService';
import { CatalogApiRequests } from './CatalogApiRequests';
import { catalogApolloClient } from './CatalogApolloClient';
import { CatalogBrowserSubspaceExplorer } from './CatalogBrowserSubspaceExplorer';
import { registerErrorStore, unregisterErrorStore } from './CatalogErrorReporterService';

type CatalogBrowserHostOwnProps = {};

export type ConnectedCatalogBrowserHostProps = SiteUrlsProps & {
  readonly areUsersVisible: boolean;
  readonly assetCountResult?: ISpaceInfoTypeItemCount<'catalog'>;
};

type CatalogBrowserHostProps =
  ConnectedCatalogBrowserHostProps &
  CatalogBrowserHostOwnProps &
  CatalogBrowserHostDispatchProps;
export interface CatalogBrowserHostDispatchProps {
  readonly fetchUpdatedAssetsCounts: () => void;
}

class CatalogBrowserHost extends React.Component<CatalogBrowserHostProps> {

  // subspaceName name here is set to a default value instead of undefined. This is in lieu of having logic in SpaceInfo to handle the case where this is undefined. database/databaseWithCrass was chosen because it is the first content type the user is shown when they enter the page. In the event that the user refreshes the External Assets page while it is showing the tables page, the subspace explorer will overwrite the subspace name set here.
  private spaceInfo: SpaceInfo = {
    spaceType: 'catalog',
    subspaceName: FeatureFlags.externalAssetsWithCRASSEnabled ? 'databaseWithCrass' : 'database',
    item: SpaceItemForNonEntitySpace
  };

  public componentDidMount() {
    if (FeatureFlags.externalAssetsWithCRASSEnabled) {
      this.props.fetchUpdatedAssetsCounts();
    }
  }

  public componentDidUpdate() {
    if (FeatureFlags.externalAssetsWithCRASSEnabled) {
      this.props.fetchUpdatedAssetsCounts();
    }
  }

  private renderDatabasesExplorer = (routeLocationSearch: string): JSX.Element => {
    const subspaceName = FeatureFlags.externalAssetsWithCRASSEnabled ? 'databaseWithCrass' : 'database';
    return <CatalogBrowserSubspaceExplorer
      key="database"
      subspaceName={subspaceName}
      routeLocationSearch={routeLocationSearch}
      contentDefinition={DatabaseDefinition}
      contentType={DatabaseContentType}
    />;
  }

  private renderTablesExplorer = (routeLocationSearch: string): JSX.Element => {
    const subspaceName = FeatureFlags.externalAssetsWithCRASSEnabled ? 'tableWithCrass' : 'table';
    return <CatalogBrowserSubspaceExplorer
      key="table"
      subspaceName={subspaceName}
      routeLocationSearch={routeLocationSearch}
      contentDefinition={TableDefinition}
      contentType={TableContentType}
    />;
  }

  public render(): JSX.Element {
    let result: WebClientApiContentTypeCountsResponse | undefined;

    if (FeatureFlags.externalAssetsWithCRASSEnabled && this.props.assetCountResult) {
      result = {
        type: 'SUCCESS',
        databasesCount: this.props.assetCountResult.databaseWithCrass,
        tablesCount: this.props.assetCountResult.tableWithCrass,
      };
    }

    return (
      <div className="tb-fill" style={{ display: 'flex' }}>
        <ConnectedSpaceInfoProvider spaceInfo={this.spaceInfo}>
          <EmbeddableCatalogBrowserWithoutRouter
            client={catalogApolloClient}
            siteUrls={this.props.urls}
            apiRequests={CatalogApiRequests}
            uiActions={DataCatalogUIActionsService.instance()}
            featureFlags={catalogFeatureFlagsAdapter()}
            renderDatabasesExplorer={this.renderDatabasesExplorer}
            renderTablesExplorer={this.renderTablesExplorer}
            registerErrorStore={registerErrorStore}
            unregisterErrorStore={unregisterErrorStore}
            newWorkbookFromCatalog={FeatureFlags.newWorkbookFromCatalog && !hideLaunchEditOnMobile()}
            newFlowFromCatalog={FeatureFlags.flowsFromCatalogEnabled && !hideLaunchEditOnMobile()}
            areUsersVisible={this.props.areUsersVisible}
            webClientResult={result}
          />
        </ConnectedSpaceInfoProvider>
      </div>
    );
  }
}

export const WrappedCatalogBrowserHost = CatalogBrowserHost;
